/**
 * Status tiap WhatsApp ke customer — T45 (25 Okt 2026). Dipakai di Permintaan Desain, FA, dan Kanal WhatsApp.
 *
 * ⛔ "Terkirim" BUKAN "sampai". Tahap dari server (`backend/services/wa_status_kirim.py`):
 *   Menunggu dikirim → Diterima layanan WA → Terkirim → Sampai di HP → Sudah dibaca, atau Gagal, atau Belum pasti
 *   (24 jam tanpa kabar sampai). Hanya "Sampai di HP" dan "Sudah dibaca" berarti customer menerimanya.
 * K206: hanya Owner, Kepala Cabang, CS yang melihat bagian ini (server menolak peran lain).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import Hint from "@/components/etams/Hint";
import { PERAN_STATUS_WA, teksTugasWa } from "@/lib/statusWa";

const WARNA = {
  DELIVERED: "border-success/40 bg-success/10 text-success",
  READ: "border-success/40 bg-success/10 text-success",
  FAILED: "border-destructive/40 bg-destructive/10 text-destructive",
  TIDAK_TERKONFIRMASI: "border-warning-soft-border bg-warning-soft text-warning-soft-foreground",
};

export function LencanaStatusWa({ item }) {
  return (
    <span className={`inline-block rounded border px-1.5 text-xs ${WARNA[item.tahap] || "border-border text-muted-foreground"}`}
          data-testid="status-wa-lencana" data-tahap={item.tahap}>
      {item.label}
    </span>
  );
}

const URUT_WAKTU = [
  ["dibuat", "Dibuat"], ["diterima_layanan", "Diterima layanan WA"], ["terkirim", "Terkirim"],
  ["sampai", "Sampai di HP"], ["dibaca", "Dibaca"], ["gagal", "Gagal"],
];

export function WaktuStatusWa({ waktu }) {
  const ada = URUT_WAKTU.filter(([k]) => waktu && waktu[k]);
  if (!ada.length) return null;
  return (
    <p className="text-xs text-muted-foreground">
      {ada.map(([k, l]) => `${l} ${formatDateTime(waktu[k])}`).join(" · ")}
    </p>
  );
}

export function BarisStatusWa({ item, rujukan }) {
  // T62: hasil tugas CS — "sudah dihubungi oleh Rina, <waktu>" sesudah dicatat, bukan tetap "menunggu".
  const tugas = teksTugasWa(item, formatDateTime);
  return (
    <div className="rounded-md border border-border p-2 text-sm space-y-0.5" data-testid="status-wa-baris">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{item.jenis}</span>
        <LencanaStatusWa item={item} />
        {tugas && (
          <span className={`text-xs ${item.tugas?.hasil || item.tugas?.selesai_sendiri ? "text-success" : "text-muted-foreground"}`}
                data-testid="status-wa-tugas">· {tugas}</span>
        )}
      </div>
      {rujukan && <p className="text-xs text-muted-foreground" data-testid="status-wa-rujukan">{rujukan}</p>}
      {item.alasan && <p className="text-xs">{item.alasan}</p>}
      <WaktuStatusWa waktu={item.waktu} />
    </div>
  );
}

/** Bagian "Kabar WhatsApp ke customer" untuk satu PD (`sumber="dr"`) atau FA (`sumber="fa"`). */
export default function StatusWaCustomer({ sumber, id }) {
  const { profile } = useAuth();
  const boleh = PERAN_STATUS_WA.includes(profile?.role?.code);
  const [items, setItems] = useState(null);
  const [galat, setGalat] = useState("");
  useEffect(() => {
    if (!boleh || !id) return undefined;
    let hidup = true;
    api.get(`/wa-status/${sumber}/${id}`)
      .then((r) => { if (hidup) setItems(r.data.items || []); })
      .catch((e) => { if (hidup) setGalat(apiErrorMessage(e, "Status WhatsApp gagal dimuat.")); });
    return () => { hidup = false; };
  }, [boleh, sumber, id]);
  if (!boleh) return null;
  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid="status-wa-customer">
      <h2 className="font-semibold text-foreground">
        Kabar WhatsApp ke customer{" "}
        <Hint>
          "Terkirim" belum berarti sampai. Customer baru menerimanya bila tertulis "Sampai di HP" atau "Sudah dibaca".
          Pesan penting yang gagal atau belum sampai dalam 15 menit menjadi tugas CS di layar Kanal WhatsApp.
        </Hint>
      </h2>
      {galat && <p className="text-sm text-destructive">{galat}</p>}
      {items === null && !galat && <p className="text-sm text-muted-foreground">Memuat…</p>}
      {items && items.length === 0 && <p className="text-sm text-muted-foreground">Belum ada WhatsApp ke customer ini.</p>}
      {(items || []).map((x) => <BarisStatusWa key={x.id} item={x} />)}
    </section>
  );
}
